/* eslint-disable no-console */
import {showNotification} from "@mantine/notifications";
import {IOmniAPIResponse} from "@/types/IOmniAPIResponse.ts";
import {isValidElement, useCallback, useState} from "react";
import { Group } from "@mantine/core";
import TooltipActionIcon from "@/components/TooltipActionIcon";
import { IconCheck, IconCopy } from "@tabler/icons-react";

interface CopyableErrorMessageProps {
    error?: string
    error_detail?: string
}

function CopyableErrorMessage({error, error_detail}: CopyableErrorMessageProps) {
    const [copied, setCopied] = useState(false);

    const copy = useCallback(() => {
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
        }, 1000);

        navigator.clipboard.writeText(`${error}\n${error_detail ?? ""}`);
    }, [setCopied, error, error_detail]);

    return (
        <Group wrap="nowrap" align="center" gap="xs">
            <TooltipActionIcon
                label={copied ? "Copied!" : "Copy Error Message"}
                onClick={copy}
                c="gray"
                actionIconProps={{
                    variant: "subtle",
                }}
                tooltipProps={{
                    zIndex: 401,
                }}
            >
                {copied ?
                    <IconCheck/>
                    :
                    <IconCopy/>
                }
            </TooltipActionIcon>
            {error_detail ?? error}
        </Group>
    );
}

interface OmniFetchTSOptions {
    returnAPIErrors: boolean
    ignoreInternalErrors: boolean
}

export default async function OmniFetchTS<ResponseDataT>(url: string, options?: RequestInit, json?: object, omniFetchOptions?: Partial<OmniFetchTSOptions>) {
    return new Promise<IOmniAPIResponse<ResponseDataT>>((resolve, reject) => {
        if (!options) {
            options = {method: "GET"}
        }
        const requestHeaders: HeadersInit = new Headers(options.headers ?? undefined)
        requestHeaders.set("Accept", "application/json")
            if (json != null) {
                options.body = JSON.stringify(json)
                requestHeaders.set("Content-Type", "application/json")
            }
        options.headers = requestHeaders
        fetch(url, options).then((resp) => {
            if (resp.headers.get("Content-Type") === "application/json") {
                void resp.json().then((json) => {
                    const js = json as unknown as IOmniAPIResponse<ResponseDataT>
                    js.response_headers = resp.headers
                    js.status_code = resp.status
                    if ((Object.prototype.hasOwnProperty.call(json, "success") && js.success) || omniFetchOptions?.returnAPIErrors) {
                        resolve(js)
                    } else if (
                        Object.prototype.hasOwnProperty.call(json, "msal_expired")
                        && js.msal_expired
                    ) {
                        window.location.assign("/msal_init?pre_auth_path="+window.location.href)
                    } else {
                        let title: string | undefined = "Error!";
                        if (js.error_detail) {
                            title = js.error;
                        }
                        const message = <CopyableErrorMessage error={js.error} error_detail={js.error_detail}/>
                        showNotification({
                            title: title,
                            message: message,
                            color: 'red',
                            autoClose: false,
                        });
                        console.error("OmniFetch Error: ", json)
                        resolve(json) // Resolve this error, as it can optionally be further handled alongside normal responses
                    }
                })
            } else {
                const errorMessage = "OmniFetch received non-JSON response! If intentional, use fetch instead to handle response data and errors correctly"
                console.error(errorMessage)
                reject({success: false, error: errorMessage})
            }
        }).catch((reason) => {
            const ignoreInternalErrors = omniFetchOptions?.ignoreInternalErrors != null ? omniFetchOptions.ignoreInternalErrors : false
            if (!ignoreInternalErrors) {
                console.error("OmniFetch Internal Error: ", json)
            }
            if (isValidElement(reason) && !ignoreInternalErrors) {
                showNotification({
                    title: 'Error!',
                    message: reason,
                    color: 'red'
                })
            } else if (!ignoreInternalErrors) {
                showNotification({
                    title: 'Error!',
                    message: `${reason}`,
                    color: 'red'
                })
            }
            reject({success: false, error: reason})
        })
    })
}
